/**
 * Очередь прокси в чекере (админка).
 * Source of truth — PostgreSQL (`admin_proxy_checker_state` через GET/PUT /api/proxy-checker/admin-queue).
 * In-memory кэш + debounce PUT; legacy localStorage мигрируется один раз при первой загрузке.
 */
import { apiService } from '../services/api';

export const ADMIN_CHECKER_QUEUE_STORAGE_KEY = 'pm_admin_checker_queue_v1';

const FLUSH_DEBOUNCE_MS = 400;
export const ADMIN_CHECKER_QUEUE_MAX_ROWS = Math.max(
  100,
  Math.min(
    500,
    Number.parseInt(process.env.REACT_APP_ADMIN_CHECKER_QUEUE_MAX_ROWS || '500', 10) || 500
  )
);

let memoryCache = null;
/** @type {boolean} */
let loadedFromApi = false;
/** @type {Promise<any[]>|null} */
let loadPromise = null;
/** @type {ReturnType<typeof setTimeout>|null} */
let flushTimer = null;
/** @type {Promise<void>|null} */
let flushPromise = null;
/** @type {Set<(rows: any[]) => void>} */
const listeners = new Set();

function capRows(rows) {
  const normalizedRows = Array.isArray(rows) ? rows : [];
  if (normalizedRows.length <= ADMIN_CHECKER_QUEUE_MAX_ROWS) {
    return normalizedRows;
  }
  return normalizedRows.slice(-ADMIN_CHECKER_QUEUE_MAX_ROWS);
}

function readLegacyLocal() {
  try {
    try {
      const legacy = sessionStorage.getItem(ADMIN_CHECKER_QUEUE_STORAGE_KEY);
      if (legacy && localStorage.getItem(ADMIN_CHECKER_QUEUE_STORAGE_KEY) == null) {
        localStorage.setItem(ADMIN_CHECKER_QUEUE_STORAGE_KEY, legacy);
      }
      if (legacy) sessionStorage.removeItem(ADMIN_CHECKER_QUEUE_STORAGE_KEY);
    } catch {
      // ignore
    }
    const raw = localStorage.getItem(ADMIN_CHECKER_QUEUE_STORAGE_KEY);
    if (!raw) return [];
    const data = JSON.parse(raw);
    return capRows(data);
  } catch {
    return [];
  }
}

function clearLegacyLocal() {
  try {
    localStorage.removeItem(ADMIN_CHECKER_QUEUE_STORAGE_KEY);
    sessionStorage.removeItem(ADMIN_CHECKER_QUEUE_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function notifyListeners() {
  const rows = Array.isArray(memoryCache) ? memoryCache : [];
  // откладываем: write часто вызывается из setState-updater React
  queueMicrotask(() => {
    listeners.forEach((fn) => {
      try {
        fn(rows);
      } catch {
        // ignore
      }
    });
  });
}

function extractRowsFromResponse(res) {
  const body = res?.data;
  if (Array.isArray(body?.rows)) return body.rows;
  if (Array.isArray(body?.data?.rows)) return body.data.rows;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body)) return body;
  return [];
}

async function putRowsToApi(rows) {
  await apiService.putAdminCheckerQueue(capRows(rows));
}

function scheduleFlush() {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushAdminCheckerQueueNow();
  }, FLUSH_DEBOUNCE_MS);
}

/**
 * @param {(rows: any[]) => void} listener
 * @returns {() => void}
 */
export function subscribeAdminCheckerQueue(listener) {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Синхронное чтение in-memory (или legacy до первой загрузки API). */
export function readAdminCheckerQueue() {
  if (Array.isArray(memoryCache)) return memoryCache;
  if (!loadedFromApi) {
    return readLegacyLocal();
  }
  return [];
}

/**
 * Запись в память + отложенный PUT в БД.
 * @param {any[]} entries
 */
export function writeAdminCheckerQueue(entries) {
  memoryCache = capRows(entries);
  loadedFromApi = true;
  notifyListeners();
  scheduleFlush();
}

export function clearAdminCheckerQueue() {
  memoryCache = [];
  loadedFromApi = true;
  clearLegacyLocal();
  notifyListeners();
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  flushPromise = putRowsToApi([])
    .catch(() => {})
    .finally(() => {
      flushPromise = null;
    });
}

/** Немедленный flush в БД (после массовых замеров / перед уходом со страницы). */
export async function flushAdminCheckerQueueNow() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (!loadedFromApi && !Array.isArray(memoryCache)) return;
  const rows = Array.isArray(memoryCache) ? memoryCache : [];
  if (flushPromise) {
    try {
      await flushPromise;
    } catch {
      // ignore previous failure
    }
  }
  const run = putRowsToApi(rows);
  flushPromise = run.catch(() => {});
  try {
    await run;
  } finally {
    flushPromise = null;
  }
}

/**
 * Загрузка из БД; при пустой БД — одноразовая миграция из localStorage.
 * @returns {Promise<any[]>}
 */
export function ensureAdminCheckerQueueLoaded() {
  if (loadedFromApi && Array.isArray(memoryCache)) {
    return Promise.resolve(memoryCache);
  }
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    try {
      const res = await apiService.getAdminCheckerQueue();
      let rows = extractRowsFromResponse(res);
      if (!Array.isArray(rows)) rows = [];

      if (rows.length === 0) {
        const legacy = readLegacyLocal();
        if (legacy.length > 0) {
          try {
            const putRes = await apiService.putAdminCheckerQueue(legacy);
            rows = extractRowsFromResponse(putRes);
            if (!Array.isArray(rows) || rows.length === 0) rows = legacy;
          } catch {
            rows = legacy;
          }
          clearLegacyLocal();
        }
      } else {
        clearLegacyLocal();
      }

      memoryCache = capRows(rows);
      loadedFromApi = true;
      notifyListeners();
      return memoryCache;
    } catch {
      const legacy = readLegacyLocal();
      memoryCache = capRows(legacy);
      loadedFromApi = true;
      notifyListeners();
      return memoryCache;
    } finally {
      loadPromise = null;
    }
  })();

  return loadPromise;
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (!loadedFromApi || !Array.isArray(memoryCache)) return;
    // best-effort: sync XHR не используем; debounce уже мог отправить
    void flushAdminCheckerQueueNow();
  });
}
